import GenericSlider from "generic-slider";

if (!customElements.get("custom-announcement-bar")) {
    class CustomAnnouncementBar extends GenericSlider {
        constructor() {
            super();
            this._originalDelay = null;
            this._autoplayActive = false;
        }

        connectedCallback() {
            super.connectedCallback();
            this.setupTextScrolling();
        }

        setupTextScrolling() {
            this.detectOverflow();
            this._resizeObserver = new ResizeObserver(() => this.detectOverflow());
            this._resizeObserver.observe(this);

            if (!this.swiper) return;

            this._originalDelay = this.swiper.params.autoplay?.delay;
            this._autoplayActive = !!this.swiper.autoplay?.running;

            // Track play/pause via the button (runs after GenericSlider's handler)
            const autoplayButton = this.querySelector(
                ".custom-announcement-bar__toggle-autoplay-button"
            );
            if (autoplayButton) {
                ["click", "keydown"].forEach((eventType) => {
                    autoplayButton.addEventListener(eventType, (e) => {
                        if (eventType === "keydown" && e.key !== "Enter" && e.key !== " ") return;
                        this._autoplayActive = this.swiper.autoplay.running;
                        if (this._autoplayActive) {
                            this.syncDelay();
                            // Restart so the timer uses the updated delay
                            this.swiper.autoplay.stop();
                            this.swiper.autoplay.start();
                        }
                    });
                });
            }

            // Advance slide when a scroll cycle completes on the active slide
            this.addEventListener("animationiteration", (e) => {
                if (!this._autoplayActive) return;
                if (e.animationName !== "announcement-scroll") return;
                if (!e.target.closest(".swiper-slide-active")) return;
                this.swiper.slideNext();
            });

            this.swiper.on("slideChange", () => {
                this.restartActiveAnimation();
            });

            // After each transition, keep autoplay alive with the correct delay
            this.swiper.on("slideChangeTransitionEnd", () => {
                if (!this._autoplayActive) return;
                this.syncDelay();
                // Force-restart autoplay timer so Swiper schedules the next advance
                this.swiper.autoplay.stop();
                this.swiper.autoplay.start();
            });

            // Initial sync
            if (this._autoplayActive) {
                this.syncDelay();
            }
        }

        detectOverflow() {
            const paragraphs = this.querySelectorAll(".simple-truncate");
            paragraphs.forEach((p) => {
                const span = p.querySelector("span");
                if (!span) return;

                // Store original text on first run
                if (!span.dataset.originalText) {
                    span.dataset.originalText = span.textContent;
                }

                // Reset to single copy for measurement
                span.textContent = span.dataset.originalText;
                p.classList.add("is-overflowing");
                span.style.animation = "none";
                span.style.transform = "translateX(0)";

                const pStyles = getComputedStyle(p);
                const contentWidth =
                    p.clientWidth -
                    parseFloat(pStyles.paddingLeft || 0) -
                    parseFloat(pStyles.paddingRight || 0);
                const singleTextWidth = span.offsetWidth;
                const overflow = singleTextWidth - contentWidth;

                if (overflow > 1) {
                    // Duplicate text with a gap for seamless marquee loop
                    const gap = 80;
                    span.textContent = "";
                    span.appendChild(document.createTextNode(span.dataset.originalText));
                    const spacer = document.createElement("span");
                    spacer.style.display = "inline-block";
                    spacer.style.width = `${gap}px`;
                    span.appendChild(spacer);
                    span.appendChild(document.createTextNode(span.dataset.originalText));

                    const scrollDistance = singleTextWidth + gap;
                    const scrollDuration = Math.max(5, scrollDistance / 50);
                    p.style.setProperty("--scroll-distance", `-${scrollDistance}px`);
                    p.style.setProperty("--scroll-duration", `${scrollDuration}s`);
                } else {
                    p.classList.remove("is-overflowing");
                    span.textContent = span.dataset.originalText;
                    p.style.removeProperty("--scroll-distance");
                    p.style.removeProperty("--scroll-duration");
                }

                span.style.animation = "";
                span.style.transform = "";
            });
        }

        syncDelay() {
            if (!this.swiper?.params?.autoplay || this._originalDelay == null) return;
            const activeSlide = this.querySelector(".swiper-slide-active");
            if (!activeSlide) return;
            if (activeSlide.querySelector(".simple-truncate.is-overflowing")) {
                this.swiper.params.autoplay.delay = 999999;
            } else {
                this.swiper.params.autoplay.delay = this._originalDelay;
            }
        }

        restartActiveAnimation() {
            const activeSlide = this.querySelector(".swiper-slide-active");
            if (!activeSlide) return;
            const span = activeSlide.querySelector(".simple-truncate.is-overflowing span");
            if (!span) return;
            span.style.animation = "none";
            span.offsetHeight;
            span.style.animation = "";
        }
    }

    customElements.define("custom-announcement-bar", CustomAnnouncementBar);
}
